
const libPictProvider = require('pict-provider');

const libMasterListCatalog = require('../master-lists/Headlight-MasterLists.js');
const libMasterListBuilder = require('../master-lists/MasterList-Builder.js');
const libMasterListCategories = require('../master-lists/MasterList-Categories.js');
const libMasterListAssociations = require('../master-lists/MasterList-Associations.js');
const libConnectionsGenerated = require('../master-lists/Connections.generated.js');
const libNavigationGraph = require('../navigation-graph.js');

// NamespaceKey under which each master list's configuration lives in the cascading Headlight
// Configuration service. ONE NamespaceValue per entity (Material, PayItem, …) — never a shared
// blob — so a Customer/User override of one list cannot suppress the others (the service diffs
// each Put against the level above and hides parent keys absent from the payload; see api/config).
// Per-entity config object: { Enabled, Label, Domain, Icon, SearchFields, Columns, ReadTabs,
// ReadLayout, BulkAssociations, MatrixAssociations, UnlinkAssociations }.
const CATALOG_NAMESPACE = 'MasterListCatalog';

// Write scopes this tool exposes. Global = the platform default (super-user only); Customer = a
// customer-wide override (CustomerAdmin); User = a personal override (anyone). Project still
// cascades on read but is not authored here — this app has no project context.
const SCOPE_LEVELS = [ 'Global', 'Customer', 'User' ];
const SCOPE_LABELS = { Global: 'Platform Default', Customer: 'Customer', User: 'My Settings' };

// Config fields the tool persists. Everything else (Keywords / Description) stays code-seed
// metadata. Arrays are written whole (the Configuration service replaces arrays wholesale).
const PERSISTED_SCALARS = [ 'Enabled', 'Label', 'Domain', 'Icon', 'ReadLayout' ];
const PERSISTED_ARRAYS = [ 'SearchFields', 'Columns', 'ReadTabs', 'BulkAssociations', 'MatrixAssociations', 'UnlinkAssociations', 'ChildLists' ];

// The four association VIEW surfaces, in priority order (used to seed the editor + serialize back).
const ASSOCIATION_VIEW_FIELDS =
[
	{ Kind: 'readtab', Field: 'ReadTabs' },
	{ Kind: 'bulk', Field: 'BulkAssociations' },
	{ Kind: 'matrix', Field: 'MatrixAssociations' },
	{ Kind: 'unlink', Field: 'UnlinkAssociations' }
];

// Columns that are bookkeeping / system fields — hidden by default when a list has no
// curated column set yet, so an auto-generated list leads with real data, not GUIDs.
const SYSTEM_COLUMN = /^(ID[A-Z]|GUID|CreateDate$|CreatingIDUser$|UpdateDate$|UpdatingIDUser$|Deleted$|DeleteDate$|DeletingIDUser$|ExternalSyncDate$|ExternalSync)/;

/**
 * MasterListRegistry — the data layer behind the master-list configuration tool.
 *
 * Holds the merged catalog (static code seed ⊕ cascading Configuration-service overrides), lets
 * an admin add / remove / recategorize / curate lists and choose how each list surfaces its
 * associations (read tab / bulk / matrix / unlink), discovers columns from an entity's Meadow
 * /Schema, applies a config to the live pict-section-recordset registration, rebuilds the Master
 * Lists navigation tree, and persists each list as a per-entity Configuration entry at the chosen
 * scope (Global / Customer / User). The Configuration service merges the layers server-side
 * (Global → Customer → Project → User), so choices cascade — no code change, no redeploy.
 */
class MasterListRegistryProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		// The static code seed, keyed by entity — the always-present baseline the cascading config
		// layers on top of (so the app works even with empty config).
		this._seedByEntity = {};
		libMasterListCatalog.forEach((pEntry) =>
		{
			if (pEntry && pEntry.Entity)
			{
				this._seedByEntity[pEntry.Entity] = pEntry;
			}
		});

		// Merged catalog keyed by entity name. Seeded from the static catalog; cascading config
		// overrides are layered on at load (loadCascadedConfigAsync).
		this.entries = {};
		this._resetEntriesToSeed();

		// Cache of discovered schema columns per entity.
		this.schemaColumns = {};

		// The working editor model for the currently-open list (null when none selected).
		this._editor = null;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get recordSetService()
	{
		return this.pict.services.PictSectionRecordSet;
	}

	// --- Session context + write-permission (mirrors the server's config gate) ---

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	get idUser()
	{
		return this.currentUser.UserID;
	}

	/**
	 * A customer-1 / role-5 super-user can write the Global platform default
	 * (the server's EndpointBase._isSuperUser).
	 * @return {Boolean}
	 */
	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	/**
	 * A user holding a role with PermissionLevel 'CustomerAdmin' can write Customer-scope config
	 * (the server's EndpointBase._hasCustomerScopedConfigWriteAccess). UserRoles is an array of
	 * role objects at runtime (the in-repo typedef saying string[] is stale).
	 * @return {Boolean}
	 */
	get hasCustomerAdmin()
	{
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin'));
	}

	/**
	 * Can the current user WRITE config at this scope? UI gate only — the server is authoritative
	 * and returns 403 if this is wrong.
	 * @param {String} pLevel - 'Global' | 'Customer' | 'User'
	 * @return {Boolean}
	 */
	canWriteScope(pLevel)
	{
		switch (pLevel)
		{
			case 'Global': return this.isSuperUser;
			case 'Customer': return this.isSuperUser || this.hasCustomerAdmin;
			case 'User': return true;
			default: return false;
		}
	}

	/**
	 * The default scope a user lands on: Platform for super-users (their job is the platform
	 * default), Customer for customer admins, else their personal settings.
	 * @return {String}
	 */
	get defaultScope()
	{
		if (this.isSuperUser) { return 'Global'; }
		if (this.hasCustomerAdmin) { return 'Customer'; }
		return 'User';
	}

	_levelIdFor(pLevel)
	{
		if (pLevel === 'Global') { return 0; }
		if (pLevel === 'User') { return this.idUser; }
		return this.idCustomer;
	}

	_scopeLabel(pLevel)
	{
		return SCOPE_LABELS[pLevel] || pLevel;
	}

	// --- REST helpers (promise wrappers over the Headlight rest client) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	// --- Cascading Configuration service (read + scoped write) ---

	/**
	 * Read an entire Configuration namespace, cascaded server-side from Global up to the current
	 * user (Global → Customer → Project → User, User-wins). Omitting NamespaceValue returns every
	 * value in one round-trip, grouped: { <NamespaceValue>: <mergedConfig>, … }.
	 * @param {String} pNamespaceKey
	 * @return {Promise<Object>}
	 */
	async _getCascadedNamespace(pNamespaceKey)
	{
		const tmpPayload = { LevelContext: 'User', LevelContextID: this.idUser, NamespaceKey: pNamespaceKey };
		const tmpResult = await this._postJSON('Configuration/Get', tmpPayload);
		return (tmpResult && (tmpResult.Document || tmpResult)) || {};
	}

	/**
	 * Write one NamespaceValue's config at a scope. SubKey IS the NamespaceValue; the service
	 * flattens the object to one row per leaf and diffs against the level above.
	 * @return {Promise<Object>}
	 */
	_putConfigSubKey(pNamespaceKey, pSubKey, pLevel, pLevelID, pConfig)
	{
		const tmpURL = `Configuration/Put/${pNamespaceKey}/${pSubKey}/${pLevel}/${pLevelID}`;
		return this._postJSON(tmpURL, { Config: pConfig });
	}

	// --- Catalog access ---

	/**
	 * The merged catalog as an array, in the static seed order with any config-only lists appended.
	 * @return {Array<Object>}
	 */
	getEntries()
	{
		const tmpSeedOrder = libMasterListCatalog.map((pEntry) => pEntry.Entity);
		const tmpKeys = Object.keys(this.entries).sort((pA, pB) =>
		{
			const tmpIA = tmpSeedOrder.indexOf(pA);
			const tmpIB = tmpSeedOrder.indexOf(pB);
			return (tmpIA < 0 ? 999 : tmpIA) - (tmpIB < 0 ? 999 : tmpIB);
		});
		return tmpKeys.map((pKey) => this.entries[pKey]);
	}

	/**
	 * Only the lists an admin has left enabled (a removed list is Enabled === false).
	 * @return {Array<Object>}
	 */
	getEnabledEntries()
	{
		return this.getEntries().filter((pEntry) => pEntry.Enabled !== false);
	}

	getEntry(pEntity)
	{
		return this.entries[pEntity];
	}

	/**
	 * Candidate entities that can be added as a list (not currently enabled, so removed
	 * ones can be re-added too).
	 * @return {Array<Object>}
	 */
	getAvailableCandidates()
	{
		return libMasterListCategories.CANDIDATES.filter((pCandidate) =>
		{
			const tmpEntry = this.entries[pCandidate.Entity];
			return !tmpEntry || (tmpEntry.Enabled === false);
		});
	}

	// --- Client merge (code seed ⊕ cascaded config; matches server: arrays replace) ---

	_isPlainObject(pValue)
	{
		return (pValue !== null) && (typeof pValue === 'object') && !Array.isArray(pValue);
	}

	/**
	 * Deep-merge `pOver` onto `pBase`: nested plain objects merge recursively; arrays and scalars
	 * from `pOver` replace `pBase` wholesale (the Configuration service treats arrays as atomic
	 * leaves, so the client must match). Returns a new object; inputs are not mutated.
	 * @param {Object} pBase
	 * @param {Object} pOver
	 * @return {Object}
	 */
	_deepMergeReplaceArrays(pBase, pOver)
	{
		if (!this._isPlainObject(pOver))
		{
			return this._isPlainObject(pBase) ? Object.assign({}, pBase) : pOver;
		}
		const tmpResult = Object.assign({}, this._isPlainObject(pBase) ? pBase : {});
		Object.keys(pOver).forEach((pKey) =>
		{
			const tmpOverValue = pOver[pKey];
			const tmpBaseValue = tmpResult[pKey];
			tmpResult[pKey] = (this._isPlainObject(tmpOverValue) && this._isPlainObject(tmpBaseValue))
				? this._deepMergeReplaceArrays(tmpBaseValue, tmpOverValue)
				: tmpOverValue;
		});
		return tmpResult;
	}

	/**
	 * Merge a cascaded config object over a code-seed entry (the effective entry the app uses).
	 * @param {Object} pSeedEntry
	 * @param {Object} pConfigEntry
	 * @return {Object}
	 */
	_mergeConfigEntry(pSeedEntry, pConfigEntry)
	{
		const tmpMerged = this._deepMergeReplaceArrays(pSeedEntry || {}, pConfigEntry || {});
		if (pSeedEntry && pSeedEntry.Entity) { tmpMerged.Entity = pSeedEntry.Entity; }
		return tmpMerged;
	}

	_resetEntriesToSeed()
	{
		this.entries = {};
		Object.keys(this._seedByEntity).forEach((pEntity) =>
		{
			this.entries[pEntity] = Object.assign({}, this._seedByEntity[pEntity]);
		});
	}

	// --- Schema discovery ---

	/**
	 * Discover the column names of an entity from its Meadow /Schema endpoint (cached).
	 * @param {String} pEntity
	 * @return {Promise<Array<String>>}
	 */
	async getSchemaColumnsAsync(pEntity)
	{
		if (this.schemaColumns[pEntity])
		{
			return this.schemaColumns[pEntity];
		}
		const tmpSchema = await this._getJSON(`${pEntity}/Schema`);
		const tmpColumns = (tmpSchema && tmpSchema.properties) ? Object.keys(tmpSchema.properties) : [];
		this.schemaColumns[pEntity] = tmpColumns;
		return tmpColumns;
	}

	// --- Cascading config load + scoped persistence ---

	/**
	 * Load the cascading catalog config (one Get for the whole MasterListCatalog namespace, merged
	 * Global → Customer → User server-side) and layer it over the static code seed.
	 * @return {Promise<void>}
	 */
	async loadCascadedConfigAsync()
	{
		this._resetEntriesToSeed();

		let tmpCatalogConfig = {};
		try
		{
			tmpCatalogConfig = await this._getCascadedNamespace(CATALOG_NAMESPACE);
		}
		catch (pError)
		{
			this.log.warn(`MasterListRegistry: catalog config read failed (using code seed only): ${pError.message || pError}`);
			tmpCatalogConfig = {};
		}

		Object.keys(tmpCatalogConfig || {}).forEach((pEntity) =>
		{
			if (!pEntity || !this._isPlainObject(tmpCatalogConfig[pEntity]))
			{
				return;
			}
			const tmpSeed = this._seedByEntity[pEntity] || { Entity: pEntity };
			this.entries[pEntity] = this._mergeConfigEntry(tmpSeed, Object.assign({ Entity: pEntity }, tmpCatalogConfig[pEntity]));
		});

		await this._overlayCustomerManifestsAsync(tmpCatalogConfig || {});
	}

	/**
	 * Layer the customer's List manifests (CustomerRecordManifests) between the Global and Customer levels.
	 * @param {Object} pCatalogConfig - the merged MasterListCatalog namespace
	 * @return {Promise<void>}
	 */
	async _overlayCustomerManifestsAsync(pCatalogConfig)
	{
		const tmpManifests = this.pict.providers.CustomerRecordManifests;
		if (!tmpManifests)
		{
			return;
		}
		try
		{
			await tmpManifests.overlayMasterListEntriesAsync(this.entries, pCatalogConfig, this._seedByEntity);
		}
		catch (pError)
		{
			this.log.warn(`MasterListRegistry: customer list manifests not applied: ${pError.message || pError}`);
		}
	}

	/**
	 * Reduce a catalog entry to the config object persisted in the Configuration service.
	 * @param {Object} pEntry
	 * @return {Object}
	 */
	_toCatalogConfig(pEntry)
	{
		const tmpConfig = {};
		PERSISTED_SCALARS.forEach((pKey) =>
		{
			if (pEntry[pKey] !== undefined) { tmpConfig[pKey] = pEntry[pKey]; }
		});
		PERSISTED_ARRAYS.forEach((pKey) =>
		{
			if (Array.isArray(pEntry[pKey])) { tmpConfig[pKey] = pEntry[pKey]; }
		});
		return tmpConfig;
	}

	/**
	 * Persist a catalog entry at a scope (Global / Customer / User), then re-read the cascaded
	 * config so local state reflects the server's diffing + cascade truthfully, and re-apply
	 * everything to the live recordset configs + nav.
	 * @param {Object} pEntry - the (partial) entry to save; merged over the current entry.
	 * @param {String} [pScopeLevel] - 'Global' | 'Customer' | 'User'
	 * @return {Promise<Object>} the merged entry after re-read.
	 */
	async saveEntryAsync(pEntry, pScopeLevel)
	{
		pScopeLevel = pScopeLevel || this.editorState.ScopeLevel || this.defaultScope;
		if (!this.canWriteScope(pScopeLevel))
		{
			throw new Error(`You do not have permission to save at the ${this._scopeLabel(pScopeLevel)} level.`);
		}

		// Persist the FULL effective entry (whole arrays — the service replaces arrays wholesale).
		const tmpEntry = Object.assign({}, this.entries[pEntry.Entity], pEntry);
		this.entries[pEntry.Entity] = tmpEntry;

		await this._putConfigSubKey(CATALOG_NAMESPACE, pEntry.Entity, pScopeLevel, this._levelIdFor(pScopeLevel), this._toCatalogConfig(tmpEntry));

		// Re-Get (no client cache): the server may have dropped leaves equal to the parent level.
		await this.loadCascadedConfigAsync();
		this.applyAllToLive();
		return this.entries[pEntry.Entity];
	}

	// --- Live application to pict-section-recordset + navigation ---

	/**
	 * Apply a catalog entry's curation (columns / search / title) and its association-view choices
	 * (read tabs / bulk screens) to the live recordset registration so the list reflects it without
	 * a reload. Registers the recordset dynamically if it is not already known.
	 * @param {Object} pEntry
	 */
	applyEntryToLiveConfig(pEntry)
	{
		const tmpService = this.recordSetService;
		if (!tmpService)
		{
			return;
		}
		let tmpConfig = tmpService.recordSetProviderConfigurations[pEntry.Entity];
		if (!tmpConfig)
		{
			const tmpBuilt = libMasterListBuilder.buildRecordSetConfiguration(pEntry);
			tmpService.loadRecordSetDynamically(tmpBuilt, pEntry.Entity);
			tmpConfig = tmpService.recordSetProviderConfigurations[pEntry.Entity] || tmpBuilt;
		}

		// Permission-expression layer (Phase 2): gate the list "New" button by the current user's create right
		// (ImprovedTabularRecordSetProvider's title template reads this flag). Covers the runtime master-list
		// rebuild path, mirroring the app's onAfterLogin stamp.
		const tmpEffectivePermissions = this.pict.fable.EffectivePermissionsService;
		if (tmpEffectivePermissions && (tmpConfig.RecordSetListShowCreateButton !== false))
		{
			tmpConfig.RecordSetListShowCreateButton = tmpEffectivePermissions.can(pEntry.Entity, 'Create');
		}

		if (Array.isArray(pEntry.Columns) && (pEntry.Columns.length > 0))
		{
			tmpConfig.RecordSetListColumns = libMasterListBuilder.markForeignKeyColumnsUnsortable(pEntry.Columns.map((pColumn) =>
				(typeof pColumn === 'string') ? { Key: pColumn, DisplayName: pColumn } : Object.assign({}, pColumn)), pEntry);
		}
		if (Array.isArray(pEntry.SearchFields) && (pEntry.SearchFields.length > 0))
		{
			tmpConfig.SearchFields = pEntry.SearchFields.slice();
		}
		if (pEntry.Label)
		{
			tmpConfig.Title = pEntry.Label;
		}

		// Association views are now cascading data (not code), so push them into the live config —
		// the recordset configs were built from the seed at construction time, before config loads.
		try
		{
			if (typeof libMasterListBuilder.applyAssociationViewsToConfig === 'function')
			{
				libMasterListBuilder.applyAssociationViewsToConfig(tmpConfig, pEntry);
			}
		}
		catch (pError)
		{
			this.log.warn(`MasterListRegistry: association-view apply failed for ${pEntry.Entity}: ${pError.message || pError}`);
		}
	}

	/**
	 * Make sure pict-section-recordset's routes cover any newly (dynamically) registered
	 * recordsets so #/PSRS/<Entity>/List resolves.
	 */
	_ensureRoutes()
	{
		try
		{
			const tmpRouter = this.pict.providers.RecordSetRouter;
			if (tmpRouter && tmpRouter.pictRouter)
			{
				tmpRouter.addRoutes(tmpRouter.pictRouter);
			}
		}
		catch (pError)
		{
			this.log.warn(`MasterListRegistry: addRoutes failed: ${pError.message || pError}`);
		}
	}

	/**
	 * Rebuild the Master Lists navigation tree (categorized) from the enabled lists and push it
	 * into the live navigation, then repaint.
	 */
	rebuildNav()
	{
		try
		{
			const tmpNav = this.pict.providers['Pict-Navigation'];
			if (!tmpNav || typeof tmpNav.setNavigationGraph !== 'function')
			{
				return;
			}
			const tmpReferenceSections = libMasterListBuilder.buildCategorizedSections(this.getEnabledEntries(), libMasterListCategories.CATEGORIES);
			const tmpGraph = (typeof libNavigationGraph.composeGraph === 'function')
				? libNavigationGraph.composeGraph(tmpReferenceSections)
				: tmpReferenceSections;
			tmpNav.setNavigationGraph(tmpGraph);
			this._repaintNav();
		}
		catch (pError)
		{
			this.log.error(`MasterListRegistry: rebuildNav failed: ${pError.message || pError}`);
		}
	}

	_repaintNav()
	{
		try
		{
			const tmpApp = this.pict.PictApplication;
			const tmpStyle = this.pict.AppData.NavRenderType
				|| (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('headlight-platform-config-nav-render-type'))
				|| 'List';
			if (tmpApp && typeof tmpApp.applyNavigationStyle === 'function')
			{
				tmpApp.applyNavigationStyle(tmpStyle);
			}
		}
		catch (pError)
		{
			// Nav repaint is best-effort; the next nav render reads the graph live regardless.
		}
	}

	/**
	 * Apply every enabled list to its live recordset config, ensure routes, and rebuild the nav.
	 */
	applyAllToLive()
	{
		this.getEnabledEntries().forEach((pEntry) =>
		{
			try
			{
				this.applyEntryToLiveConfig(pEntry);
			}
			catch (pError)
			{
				this.log.error(`MasterListRegistry: apply failed for ${pEntry.Entity}: ${pError.message || pError}`);
			}
		});
		this._ensureRoutes();
		this.rebuildNav();
	}

	/**
	 * Boot hook: load the cascaded config, apply every enabled list to its live recordset config,
	 * ensure routes exist for dynamically-registered lists, and rebuild the nav. Called from the
	 * application's onAfterLoginAsync.
	 * @return {Promise<void>}
	 */
	async loadAndApplyAllAsync()
	{
		await this.loadCascadedConfigAsync();
		this.applyAllToLive();
		const tmpManifests = this.pict.providers.CustomerRecordManifests;
		if (tmpManifests)
		{
			try
			{
				await tmpManifests.applyFilterOverlaysAsync();
			}
			catch (pError)
			{
				this.log.warn(`MasterListRegistry: customer filter manifests not applied: ${pError.message || pError}`);
			}
		}
	}

	// --- Add / remove / categorize ---

	/**
	 * Add an entity as a managed list: discover its columns, default to a sensible
	 * (system-hidden) column set, register it, persist at the current scope, route it, rebuild the
	 * nav, then open it for editing.
	 * @param {String} pEntity
	 * @param {String} [pDomain]
	 * @return {Promise<void>}
	 */
	async addList(pEntity, pDomain)
	{
		pEntity = (pEntity || '').trim();
		const tmpState = this.editorState;
		if (!pEntity)
		{
			return;
		}

		let tmpColumns = [];
		try
		{
			tmpColumns = await this.getSchemaColumnsAsync(pEntity);
		}
		catch (pError)
		{
			tmpState.Message = `Could not add “${pEntity}” — no schema found (${pError.message || pError}).`;
			tmpState.MessageClass = 'is-danger';
			this._render();
			return;
		}

		const tmpCandidate = libMasterListCategories.CANDIDATES.find((pCandidate) => pCandidate.Entity === pEntity);
		const tmpEntry =
		{
			Entity: pEntity,
			Label: (this.entries[pEntity] && this.entries[pEntity].Label) || (tmpCandidate && tmpCandidate.Label) || pEntity,
			Domain: pDomain || (tmpCandidate && tmpCandidate.Domain) || 'Uncategorized',
			Icon: (tmpCandidate && tmpCandidate.Icon) || 'fas fa-list',
			Enabled: true,
			Columns: tmpColumns.filter((pKey) => !SYSTEM_COLUMN.test(pKey)).map((pKey) => ({ Key: pKey, DisplayName: pKey })),
			SearchFields: (tmpColumns.indexOf('Name') >= 0) ? [ 'Name' ] : []
		};

		// Optimistic: register + apply now so the session reflects it instantly, then persist.
		this.applyEntryToLiveConfig(tmpEntry);
		this._ensureRoutes();
		try
		{
			await this.saveEntryAsync(tmpEntry, tmpState.ScopeLevel);
			tmpState.Message = `Added “${tmpEntry.Label}” at the ${this._scopeLabel(tmpState.ScopeLevel)} level.`;
			tmpState.MessageClass = 'is-success';
		}
		catch (pError)
		{
			tmpState.Message = `Added “${pEntity}” to this session, but saving failed: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-warning';
		}
		tmpState.AddOpen = false;
		tmpState.AddEntity = '';
		this.rebuildNav();
		await this.selectList(pEntity);
	}

	addFromCandidate(pEntity)
	{
		const tmpCandidate = libMasterListCategories.CANDIDATES.find((pCandidate) => pCandidate.Entity === pEntity);
		this.addList(pEntity, tmpCandidate ? tmpCandidate.Domain : this.editorState.AddDomain);
	}

	addFromForm()
	{
		const tmpState = this.editorState;
		this.addList((tmpState.AddEntity || '').trim(), tmpState.AddDomain);
	}

	/**
	 * Remove a list (after a modal confirm): persist Enabled === false at the current scope, drop
	 * it from the editor + nav. The PSRS registration is left in place (the framework has no
	 * unregister), but nothing points at it.
	 * @param {String} pEntity
	 */
	removeList(pEntity)
	{
		const tmpEntry = this.getEntry(pEntity) || { Entity: pEntity, Label: pEntity };
		const tmpApply = async () =>
		{
			const tmpState = this.editorState;
			try
			{
				await this.saveEntryAsync(Object.assign({}, tmpEntry, { Entity: pEntity, Enabled: false }), tmpState.ScopeLevel);
				if (this._editor && (this._editor.entity === pEntity))
				{
					this._editor = null;
				}
				tmpState.Message = `Removed “${tmpEntry.Label || pEntity}” at the ${this._scopeLabel(tmpState.ScopeLevel)} level. Add it back any time.`;
				tmpState.MessageClass = 'is-info';
			}
			catch (pError)
			{
				tmpState.Message = `Could not remove “${tmpEntry.Label || pEntity}”: ${pError.message || pError}`;
				tmpState.MessageClass = 'is-danger';
			}
			this._render();
		};

		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && (typeof tmpModal.confirm === 'function'))
		{
			tmpModal.confirm(`Remove “${tmpEntry.Label || pEntity}” from the managed lists? You can add it back later.`,
				{ title: 'Remove list', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { tmpApply(); } });
		}
		else
		{
			tmpApply();
		}
	}

	/**
	 * Recategorize a list at the current scope and repaint the nav grouping.
	 * @param {String} pEntity
	 * @param {String} pDomain
	 * @return {Promise<void>}
	 */
	async setDomain(pEntity, pDomain)
	{
		const tmpEntry = Object.assign({}, this.getEntry(pEntity), { Entity: pEntity, Domain: pDomain });
		if (this._editor && (this._editor.entity === pEntity))
		{
			this._editor.domain = pDomain;
		}
		try
		{
			await this.saveEntryAsync(tmpEntry, this.editorState.ScopeLevel);
		}
		catch (pError)
		{
			this.editorState.Message = `Could not save the category: ${pError.message || pError}`;
			this.editorState.MessageClass = 'is-danger';
		}
		this._render();
	}

	// --- Association-view model helpers ---

	/**
	 * The associations a given entity participates in (it is one of the two sides), with the other
	 * side's label, from the code association registry.
	 * @param {String} pEntity
	 * @return {Array<{ Hash: String, OtherLabel: String }>}
	 */
	_associationsForEntity(pEntity)
	{
		const tmpRegistry = libMasterListAssociations.ASSOCIATIONS || {};
		const tmpList = [];
		Object.keys(tmpRegistry).forEach((pHash) =>
		{
			const tmpAssoc = tmpRegistry[pHash] || {};
			const tmpSideA = tmpAssoc.SideA || {};
			const tmpSideB = tmpAssoc.SideB || {};
			let tmpOther = null;
			if (tmpSideA.RecordSet === pEntity) { tmpOther = tmpSideB.RecordSet; }
			else if (tmpSideB.RecordSet === pEntity) { tmpOther = tmpSideA.RecordSet; }
			else { return; }
			tmpList.push({ Hash: pHash, OtherLabel: tmpOther || pHash });
		});
		return tmpList;
	}

	/**
	 * Read an entry's current view state for one association into the editor model: which view
	 * surfaces are on (a single association may appear in several) and a representative title.
	 * @param {Object} pEntry
	 * @param {String} pHash
	 * @param {String} pOtherLabel
	 * @return {Object}
	 */
	_readAssociationModel(pEntry, pHash, pOtherLabel)
	{
		const tmpModel = { readtab: false, bulk: false, matrix: false, unlink: false, title: '' };
		ASSOCIATION_VIEW_FIELDS.forEach((pView) =>
		{
			const tmpArray = Array.isArray(pEntry[pView.Field]) ? pEntry[pView.Field] : [];
			const tmpRecord = tmpArray.find((pItem) => pItem && (pItem.Association === pHash));
			if (tmpRecord)
			{
				tmpModel[pView.Kind] = true;
				if (!tmpModel.title && tmpRecord.Title) { tmpModel.title = tmpRecord.Title; }
			}
		});
		if (!tmpModel.title) { tmpModel.title = pOtherLabel || pHash; }
		return tmpModel;
	}

	/**
	 * Serialize the editor's association model back into the four view arrays on an entry.
	 * @param {Object} pEntry - mutated in place with ReadTabs/Bulk/Matrix/Unlink/ReadLayout.
	 */
	_writeAssociationModel(pEntry)
	{
		const tmpArrays = { readtab: [], bulk: [], matrix: [], unlink: [] };
		this._associationsForEntity(this._editor.entity).forEach((pAssoc) =>
		{
			const tmpModel = (this._editor.associations && this._editor.associations[pAssoc.Hash]) || {};
			const tmpTitle = (tmpModel.title || '').trim() || pAssoc.OtherLabel || pAssoc.Hash;
			ASSOCIATION_VIEW_FIELDS.forEach((pView) =>
			{
				if (tmpModel[pView.Kind])
				{
					tmpArrays[pView.Kind].push({ Association: pAssoc.Hash, Title: tmpTitle });
				}
			});
		});
		pEntry.ReadTabs = tmpArrays.readtab;
		pEntry.BulkAssociations = tmpArrays.bulk;
		pEntry.MatrixAssociations = tmpArrays.matrix;
		pEntry.UnlinkAssociations = tmpArrays.unlink;
		pEntry.ReadLayout = this._editor.readLayout || 'Split';
	}

	// --- Child-list (1:N FK) model helpers ---

	/**
	 * The FK child lists an entity can surface, from the generated connection graph. Each is keyed by
	 * RecordSet:FilterField (unique even when two foreign keys point at the same child entity).
	 * @param {String} pEntity
	 * @return {Array<{ Key, RecordSet, FilterField, DefaultTitle }>}
	 */
	_childListsForEntity(pEntity)
	{
		const tmpConnections = (libConnectionsGenerated.EntityConnections || {})[pEntity] || {};
		return (Array.isArray(tmpConnections.ChildLists) ? tmpConnections.ChildLists : []).map((pChild) =>
			({ Key: `${ pChild.RecordSet }:${ pChild.FilterField }`, RecordSet: pChild.RecordSet, FilterField: pChild.FilterField, DefaultTitle: pChild.Title }));
	}

	/**
	 * Read an entry's child-list choices into the editor model (which are on + their titles). An entry
	 * with NO ChildLists array is unconfigured → default every available child list on (the seed default).
	 * @param {Object} pEntry
	 * @param {String} pEntity
	 * @return {Object} keyed by RecordSet:FilterField → { on, title }
	 */
	_readChildListModel(pEntry, pEntity)
	{
		const tmpAvailable = this._childListsForEntity(pEntity);
		const tmpHasArray = Array.isArray(pEntry.ChildLists);
		const tmpByKey = {};
		if (tmpHasArray)
		{
			pEntry.ChildLists.forEach((pItem) =>
			{
				if (pItem && pItem.RecordSet) { tmpByKey[`${ pItem.RecordSet }:${ pItem.FilterField }`] = pItem; }
			});
		}
		const tmpModel = {};
		tmpAvailable.forEach((pChild) =>
		{
			const tmpItem = tmpByKey[pChild.Key];
			tmpModel[pChild.Key] = { on: tmpHasArray ? !!tmpItem : true, title: (tmpItem && tmpItem.Title) || pChild.DefaultTitle };
		});
		return tmpModel;
	}

	/**
	 * Serialize the editor's child-list model back onto an entry (only the on ones, minimal shape — the
	 * ChildListTab view instances carry the display columns; the builder needs just RecordSet/FilterField/Title).
	 * @param {Object} pEntry - mutated in place with ChildLists.
	 */
	_writeChildListModel(pEntry)
	{
		const tmpChildLists = [];
		this._childListsForEntity(this._editor.entity).forEach((pChild) =>
		{
			const tmpModel = (this._editor.childLists && this._editor.childLists[pChild.Key]) || {};
			if (tmpModel.on)
			{
				tmpChildLists.push({ RecordSet: pChild.RecordSet, FilterField: pChild.FilterField, Title: (tmpModel.title || '').trim() || pChild.DefaultTitle });
			}
		});
		pEntry.ChildLists = tmpChildLists;
	}

	// --- Editor state (drives the MasterListManager view via AppData) ---

	get editorState()
	{
		if (!this.pict.AppData.MasterListManager)
		{
			this.pict.AppData.MasterListManager =
			{
				RailGroups: [], SelectedEntity: null, SelectedLabel: '', Title: '',
				Columns: [], Loading: false, Saving: false, Message: '', MessageClass: '',
				EditorSlot: [], EmptySlot: [ {} ], LoadingSlot: [], MessageSlot: [],
				AddOpen: false, AddOpenSlot: [], AddEntity: '', AddDomain: '',
				CandidateGroups: [], AddCategoryOptions: [], EditorCategoryOptions: [],
				ScopeLevel: '', ScopeOptions: [], ScopeHint: '', ScopeHintSlot: [], CanWriteCurrentScope: true,
				Associations: [], AssociationSlot: [], ReadLayoutOptions: [], ReadLayoutSlot: [], ShowReadLayout: false
			};
		}
		return this.pict.AppData.MasterListManager;
	}

	_sortByCategory(pNames)
	{
		const tmpKnown = libMasterListCategories.CATEGORIES.map((pCategory) => pCategory.Name);
		return pNames.sort((pA, pB) =>
		{
			const tmpIA = tmpKnown.indexOf(pA);
			const tmpIB = tmpKnown.indexOf(pB);
			return (tmpIA < 0 ? 999 : tmpIA) - (tmpIB < 0 ? 999 : tmpIB);
		});
	}

	_categoryIcon(pName)
	{
		const tmpCategory = libMasterListCategories.CATEGORIES.find((pCategory) => pCategory.Name === pName);
		return (tmpCategory && tmpCategory.Icon) || 'fas fa-folder';
	}

	/**
	 * Is this entry customized vs the code seed (any persisted field overridden)? Coarse — drives
	 * the "customized" badge without per-level provenance reads.
	 * @param {Object} pEntry
	 * @return {Boolean}
	 */
	_isCustomized(pEntry)
	{
		const tmpSeed = this._seedByEntity[pEntry.Entity];
		if (!tmpSeed) { return true; }
		return JSON.stringify(this._toCatalogConfig(pEntry)) !== JSON.stringify(this._toCatalogConfig(tmpSeed));
	}

	/**
	 * Recompute the AppData the view renders from the working editor model.
	 */
	deriveRenderState()
	{
		const tmpState = this.editorState;
		const tmpSelectedEntity = this._editor && this._editor.entity;

		// Scope: default to the user's natural scope; recompute write-ability + the read-only hint.
		if (!tmpState.ScopeLevel) { tmpState.ScopeLevel = this.defaultScope; }
		tmpState.ScopeOptions = SCOPE_LEVELS.map((pLevel) =>
			(
				{
					Key: pLevel,
					Label: SCOPE_LABELS[pLevel],
					SelectedAttr: (pLevel === tmpState.ScopeLevel) ? 'selected' : '',
					DisabledAttr: this.canWriteScope(pLevel) ? '' : 'disabled'
				}));
		tmpState.CanWriteCurrentScope = this.canWriteScope(tmpState.ScopeLevel);
		tmpState.ScopeHint = tmpState.CanWriteCurrentScope ? ''
			: `You can view ${this._scopeLabel(tmpState.ScopeLevel)} settings but not change them. Switch to “My Settings”, or ask an administrator.`;
		tmpState.ScopeHintSlot = tmpState.ScopeHint ? [ { ScopeHint: tmpState.ScopeHint } ] : [];

		// Left rail: enabled lists grouped by category.
		const tmpByDomain = {};
		this.getEnabledEntries().forEach((pEntry) =>
		{
			const tmpDomain = pEntry.Domain || 'Uncategorized';
			if (!tmpByDomain[tmpDomain]) { tmpByDomain[tmpDomain] = []; }
			tmpByDomain[tmpDomain].push(pEntry);
		});
		tmpState.RailGroups = this._sortByCategory(Object.keys(tmpByDomain)).map((pDomain) =>
			(
				{
					Category: pDomain,
					Icon: this._categoryIcon(pDomain),
					Lists: tmpByDomain[pDomain].map((pEntry) =>
						(
							{
								Entity: pEntry.Entity,
								Label: pEntry.Label || pEntry.Entity,
								Icon: pEntry.Icon || 'fas fa-list',
								CuratedBadge: this._isCustomized(pEntry)
									? '<span class="tag is-info is-light ml-2">customized</span>' : '',
								RowClass: (pEntry.Entity === tmpSelectedEntity) ? 'is-active' : ''
							}))
				}));

		// Add panel: candidate entities grouped by category.
		const tmpCandByDomain = {};
		this.getAvailableCandidates().forEach((pCandidate) =>
		{
			const tmpDomain = pCandidate.Domain || 'Uncategorized';
			if (!tmpCandByDomain[tmpDomain]) { tmpCandByDomain[tmpDomain] = []; }
			tmpCandByDomain[tmpDomain].push(pCandidate);
		});
		tmpState.CandidateGroups = this._sortByCategory(Object.keys(tmpCandByDomain)).map((pDomain) =>
			(
				{
					Category: pDomain,
					Icon: this._categoryIcon(pDomain),
					Entities: tmpCandByDomain[pDomain].map((pCandidate) =>
						({ Entity: pCandidate.Entity, Label: pCandidate.Label || pCandidate.Entity, Icon: pCandidate.Icon || 'fas fa-list' }))
				}));

		if (!tmpState.AddDomain)
		{
			tmpState.AddDomain = (libMasterListCategories.CATEGORIES[0] || {}).Name || 'Uncategorized';
		}
		tmpState.AddOpenSlot = tmpState.AddOpen ? [ {} ] : [];
		tmpState.AddCategoryOptions = libMasterListCategories.CATEGORIES.map((pCategory) =>
			({ Name: pCategory.Name, SelectedAttr: (pCategory.Name === tmpState.AddDomain) ? 'selected' : '' }));

		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message, MessageClass: tmpState.MessageClass || 'is-info' } ] : [];
		tmpState.SaveDisabledAttr = (tmpState.Saving || !tmpState.CanWriteCurrentScope) ? 'disabled' : '';

		if (tmpState.Loading)
		{
			tmpState.LoadingSlot = [ {} ];
			tmpState.EditorSlot = [];
			tmpState.EmptySlot = [];
		}
		else if (this._editor)
		{
			tmpState.Columns = this._editor.columns.map((pColumn) =>
				Object.assign({}, pColumn,
					{
						VisibleAttr: pColumn.Visible ? 'checked' : '',
						SearchAttr: pColumn.Searchable ? 'checked' : '',
						RowClass: pColumn.Visible ? '' : 'has-text-grey',
						SystemBadge: pColumn.IsSystem ? '<span class="tag is-light ml-1">system</span>' : ''
					}));
			tmpState.Title = this._editor.title;
			tmpState.SelectedEntity = this._editor.entity;
			tmpState.SelectedLabel = this._editor.label;
			tmpState.VisibleCount = this._editor.columns.filter((pColumn) => pColumn.Visible).length;
			tmpState.SearchCount = this._editor.columns.filter((pColumn) => pColumn.Searchable).length;
			tmpState.EditorCategoryOptions = libMasterListCategories.CATEGORIES.map((pCategory) =>
				({ Name: pCategory.Name, SelectedAttr: (pCategory.Name === this._editor.domain) ? 'selected' : '' }));

			// Association-view editor: one row per association this entity participates in.
			const tmpAssociations = this._associationsForEntity(this._editor.entity);
			tmpState.AssociationSlot = tmpAssociations.length ? [ {} ] : [];
			tmpState.Associations = tmpAssociations.map((pAssoc) =>
			{
				const tmpModel = (this._editor.associations && this._editor.associations[pAssoc.Hash]) || {};
				return (
					{
						Hash: pAssoc.Hash,
						OtherLabel: pAssoc.OtherLabel,
						Title: tmpModel.title || '',
						ReadTabAttr: tmpModel.readtab ? 'checked' : '',
						BulkAttr: tmpModel.bulk ? 'checked' : '',
						MatrixAttr: tmpModel.matrix ? 'checked' : '',
						UnlinkAttr: tmpModel.unlink ? 'checked' : ''
					});
			});
			tmpState.ShowReadLayout = tmpAssociations.some((pAssoc) =>
			{
				const tmpModel = this._editor.associations && this._editor.associations[pAssoc.Hash];
				return tmpModel && tmpModel.readtab;
			});
			tmpState.ReadLayoutSlot = tmpState.ShowReadLayout ? [ {} ] : [];
			tmpState.ReadLayoutOptions = [ 'Split', 'Tab', 'Basic' ].map((pLayout) =>
				({ Value: pLayout, SelectedAttr: (pLayout === (this._editor.readLayout || 'Split')) ? 'selected' : '' }));

			// Child-list (1:N FK) editor: one row per FK child list this entity can surface.
			const tmpChildLists = this._childListsForEntity(this._editor.entity);
			tmpState.ChildListSlot = tmpChildLists.length ? [ {} ] : [];
			tmpState.ChildLists = tmpChildLists.map((pChild) =>
			{
				const tmpModel = (this._editor.childLists && this._editor.childLists[pChild.Key]) || {};
				return (
					{
						Key: pChild.Key,
						OtherLabel: pChild.RecordSet,
						ChildOnAttr: tmpModel.on ? 'checked' : '',
						Title: tmpModel.title || ''
					});
			});

			tmpState.LoadingSlot = [];
			tmpState.EditorSlot = [ tmpState ];
			tmpState.EmptySlot = [];
		}
		else
		{
			tmpState.LoadingSlot = [];
			tmpState.EditorSlot = [];
			tmpState.EmptySlot = [ {} ];
		}
	}

	_render()
	{
		this.deriveRenderState();
		if (this.pict.views.MasterListManager)
		{
			this.pict.views.MasterListManager.render();
		}
	}

	// --- Scope control ---

	setScope(pValue)
	{
		this.editorState.ScopeLevel = pValue;
		this.editorState.Message = '';
		this._render();
	}

	// --- Add panel controls ---

	toggleAddPanel(pForce)
	{
		const tmpState = this.editorState;
		tmpState.AddOpen = (typeof pForce === 'boolean') ? pForce : !tmpState.AddOpen;
		tmpState.Message = '';
		this._render();
	}

	setAddEntity(pValue) { this.editorState.AddEntity = pValue; }
	setAddDomain(pValue) { this.editorState.AddDomain = pValue; }

	// --- Column editor ---

	/**
	 * Open a list for editing: discover its schema, seed the working model from the current
	 * (merged) entry — columns plus the association-view choices.
	 * @param {String} pEntity
	 */
	async selectList(pEntity)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		tmpState.Message = '';
		this._render();

		let tmpSchemaColumns = [];
		try
		{
			tmpSchemaColumns = await this.getSchemaColumnsAsync(pEntity);
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the schema for ${pEntity}: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
			return;
		}

		const tmpEntry = this.getEntry(pEntity) || { Entity: pEntity, Label: pEntity };
		const tmpConfig = (this.recordSetService && this.recordSetService.recordSetProviderConfigurations[pEntity]) || {};

		const tmpCurated = (Array.isArray(tmpEntry.Columns) && tmpEntry.Columns.length > 0)
			? tmpEntry.Columns
			: (Array.isArray(tmpConfig.RecordSetListColumns) ? tmpConfig.RecordSetListColumns : null);
		const tmpCuratedKeys = tmpCurated ? tmpCurated.map((pColumn) => (typeof pColumn === 'string') ? pColumn : pColumn.Key) : null;
		const tmpLabelByKey = {};
		if (tmpCurated)
		{
			tmpCurated.forEach((pColumn) =>
			{
				if (typeof pColumn === 'object' && pColumn.DisplayName)
				{
					tmpLabelByKey[pColumn.Key] = pColumn.DisplayName;
				}
			});
		}
		const tmpSearchFields = Array.isArray(tmpEntry.SearchFields) ? tmpEntry.SearchFields
			: (Array.isArray(tmpConfig.SearchFields) ? tmpConfig.SearchFields : [ 'Name' ]);

		const tmpColumnByKey = {};
		tmpSchemaColumns.forEach((pKey) =>
		{
			tmpColumnByKey[pKey] =
			{
				Key: pKey,
				DisplayName: tmpLabelByKey[pKey] || pKey,
				IsSystem: SYSTEM_COLUMN.test(pKey),
				Visible: tmpCuratedKeys ? (tmpCuratedKeys.indexOf(pKey) >= 0) : !SYSTEM_COLUMN.test(pKey),
				Searchable: tmpSearchFields.indexOf(pKey) >= 0
			};
		});

		let tmpOrdered = [];
		if (tmpCuratedKeys)
		{
			tmpCuratedKeys.forEach((pKey) => { if (tmpColumnByKey[pKey]) { tmpOrdered.push(tmpColumnByKey[pKey]); } });
			tmpSchemaColumns.forEach((pKey) => { if (tmpCuratedKeys.indexOf(pKey) < 0) { tmpOrdered.push(tmpColumnByKey[pKey]); } });
		}
		else
		{
			tmpOrdered = tmpSchemaColumns.map((pKey) => tmpColumnByKey[pKey]);
		}

		// Seed the association-view model from the merged entry.
		const tmpAssociationModel = {};
		this._associationsForEntity(pEntity).forEach((pAssoc) =>
		{
			tmpAssociationModel[pAssoc.Hash] = this._readAssociationModel(tmpEntry, pAssoc.Hash, pAssoc.OtherLabel);
		});

		this._editor =
		{
			entity: pEntity,
			label: tmpEntry.Label || pEntity,
			title: tmpEntry.Label || pEntity,
			domain: tmpEntry.Domain || 'Uncategorized',
			columns: tmpOrdered,
			associations: tmpAssociationModel,
			childLists: this._readChildListModel(tmpEntry, pEntity),
			readLayout: tmpEntry.ReadLayout || 'Split'
		};
		tmpState.Loading = false;
		this._render();
	}

	_findColumn(pKey)
	{
		return this._editor ? this._editor.columns.find((pColumn) => pColumn.Key === pKey) : null;
	}

	toggleVisible(pKey)
	{
		const tmpColumn = this._findColumn(pKey);
		if (tmpColumn) { tmpColumn.Visible = !tmpColumn.Visible; this._render(); }
	}

	toggleSearch(pKey)
	{
		const tmpColumn = this._findColumn(pKey);
		if (tmpColumn) { tmpColumn.Searchable = !tmpColumn.Searchable; this._render(); }
	}

	setColumnLabel(pKey, pValue)
	{
		const tmpColumn = this._findColumn(pKey);
		if (tmpColumn) { tmpColumn.DisplayName = pValue; }
	}

	setTitle(pValue)
	{
		if (this._editor) { this._editor.title = pValue; }
	}

	setEditorDomain(pValue)
	{
		if (this._editor) { this.setDomain(this._editor.entity, pValue); }
	}

	// --- Association-view editor controls ---

	/**
	 * Toggle whether an association is surfaced through a given view (read tab / bulk / matrix /
	 * unlink). A single association can be on in several views at once.
	 * @param {String} pHash
	 * @param {String} pKind - 'readtab' | 'bulk' | 'matrix' | 'unlink'
	 * @param {Boolean} pOn
	 */
	setAssociationView(pHash, pKind, pOn)
	{
		if (!this._editor) { return; }
		if (!this._editor.associations[pHash])
		{
			this._editor.associations[pHash] = { readtab: false, bulk: false, matrix: false, unlink: false, title: '' };
		}
		this._editor.associations[pHash][pKind] = !!pOn;
		this._render();
	}

	setAssociationTitle(pHash, pValue)
	{
		if (this._editor && this._editor.associations[pHash])
		{
			this._editor.associations[pHash].title = pValue;
		}
	}

	setReadLayout(pValue)
	{
		if (this._editor) { this._editor.readLayout = pValue; }
	}

	setChildListView(pKey, pOn)
	{
		if (!this._editor) { return; }
		if (!this._editor.childLists[pKey]) { this._editor.childLists[pKey] = { on: false, title: '' }; }
		this._editor.childLists[pKey].on = !!pOn;
		this._render();
	}

	setChildListTitle(pKey, pValue)
	{
		if (this._editor && this._editor.childLists[pKey]) { this._editor.childLists[pKey].title = pValue; }
	}

	_move(pKey, pDelta)
	{
		if (!this._editor) { return; }
		const tmpColumns = this._editor.columns;
		const tmpIndex = tmpColumns.findIndex((pColumn) => pColumn.Key === pKey);
		const tmpTarget = tmpIndex + pDelta;
		if (tmpIndex < 0 || tmpTarget < 0 || tmpTarget >= tmpColumns.length) { return; }
		const tmpMoved = tmpColumns.splice(tmpIndex, 1)[0];
		tmpColumns.splice(tmpTarget, 0, tmpMoved);
		this._render();
	}

	moveUp(pKey) { this._move(pKey, -1); }
	moveDown(pKey) { this._move(pKey, 1); }

	/**
	 * Persist the working editor (columns + association-view choices) at the current scope and
	 * apply it to the live list.
	 */
	async save()
	{
		if (!this._editor) { return; }
		const tmpState = this.editorState;
		if (!this.canWriteScope(tmpState.ScopeLevel))
		{
			tmpState.Message = `You do not have permission to save at the ${this._scopeLabel(tmpState.ScopeLevel)} level.`;
			tmpState.MessageClass = 'is-warning';
			this._render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Message = '';
		this._render();

		const tmpVisible = this._editor.columns.filter((pColumn) => pColumn.Visible);
		const tmpEntry = Object.assign({}, this.getEntry(this._editor.entity),
			{
				Entity: this._editor.entity,
				Label: (this._editor.title || '').trim() || this._editor.entity,
				Domain: this._editor.domain || 'Uncategorized',
				Enabled: true,
				Columns: tmpVisible.map((pColumn) => ({ Key: pColumn.Key, DisplayName: pColumn.DisplayName || pColumn.Key })),
				SearchFields: this._editor.columns.filter((pColumn) => pColumn.Searchable).map((pColumn) => pColumn.Key)
			});
		this._writeAssociationModel(tmpEntry);
		this._writeChildListModel(tmpEntry);

		try
		{
			await this.saveEntryAsync(tmpEntry, tmpState.ScopeLevel);
			this._editor.label = tmpEntry.Label;
			tmpState.Saving = false;
			tmpState.Message = `Saved “${tmpEntry.Label}” at the ${this._scopeLabel(tmpState.ScopeLevel)} level — ${tmpVisible.length} column(s) shown.`;
			tmpState.MessageClass = 'is-success';
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Save failed: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
		}
		this._render();
	}

	/**
	 * Navigate to the live list to preview the current configuration.
	 * @param {String} pEntity
	 */
	openList(pEntity)
	{
		window.location.hash = `#/PSRS/${pEntity}/List`;
	}
}

module.exports = MasterListRegistryProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'MasterListRegistry',
	AutoInitialize: true,
	AutoInitializeOrdinal: 200
};
